import ReactSplit, { SplitDirection } from "@devbookhq/splitter"
import { Container, GlobalStyles, Grid, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { BrowserTypes, browserName, isMobileOnly } from "react-device-detect"
import { useTranslation } from "react-i18next"

import LeftSideNavbar from "../components/Editor/LeftSideNavbar"
import Preview from "../components/Editor/Preview"
import RightSideNavbar from "../components/Editor/RightSideNavbar"
import TimelineNavbar from "../components/Editor/TimelineNavbar"
import OfflineBanner from "../components/OfflineBanner"
import { withErrorBoundary } from "../components/withErrorBoundary"
import useId from "../hooks/useId"
import { useAppDispatch, useAppSelector, useAppStore } from "../hooks/useRedux"
import { getGraphicAndProcessAsync } from "../store/slices/graphic.slice"

import { unstable_BlockerFunction as BlockerFunction, useNavigate } from "react-router-dom"
import CanvasSettingDialog from "../components/Editor/Preview/CanvasSettingDialog"
import AlertDialog from "../components/common/AlertDialog"
import LoadingBackdrop from "../components/common/LoadingBackdrop"
import LoadingBackdropWithLabel from "../components/common/LoadingBackdropWithLabel"
import Prompt from "../components/common/Prompt"
import TransitionAlerts from "../components/common/TransitionAlert"
import {
    MAIN_PANE_CLASS,
    MAIN_PANE_MIN_WIDTH,
    RIGHT_PANEL_MAX_WIDTH,
    RIGHT_PANEL_MIN_WIDTH,
    RIGHT_PANE_CLASS,
} from "../constants/editorLayout"
import { Status } from "../constants/status"
import { selectCanUndo, selectUndoable } from "../helpers/selector.helpers"
import { canSkipMasterTimelineRebuild } from "../helpers/timelineRebuild.helpers"
import { useCheckEditor } from "../hooks/useCheckEditor"
import useEditor from "../hooks/useEditor"
import { useOnlineStatus } from "../hooks/useOnlineStatus"
import { useShortcuts } from "../hooks/useShortCuts"
import useStorageLimit from "../hooks/useStorageLimit"
import { useWorkspacePermission } from "../hooks/useWorkspacePermission"
import { CompanyT } from "../store/slices/company.slice"
import { clearCompanyStatsById } from "../store/slices/companyStats.slice"
import {
    SettingsKey,
    clearAndReset,
    getPreferencesAsync,
    setOpenCanvasSettings,
    setPreferencesAsync,
} from "../store/slices/editor.slice"
import { updateMasterTimeline, updateObjectTweens } from "../store/slices/masterTimeline.slice"
import { getTimelinePreferencesAsync } from "../store/slices/timelinePreferences.slice"

const Editor = () => {
    useShortcuts()
    const { isOnline } = useOnlineStatus()

    const { t } = useTranslation()

    const canUndo = useAppSelector((state) => selectCanUndo(state))
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const graphic = useAppSelector((state) => state.graphic)
    const assets = useAppSelector((state) => state.assets.data)

    const preferences = useAppSelector((state) => state.editor.value.settings)
    const loading = useAppSelector((state) => state.loading.value)
    const percentLoading = useAppSelector((state) => state.loading.valuePercent)

    const { id, setId } = useId()
    const { usedStoragePercent, isStorageLimitExceeded } = useStorageLimit(
        graphic.data.company as CompanyT | undefined
    )

    useEffect(() => {
        if (id) {
            dispatch(getGraphicAndProcessAsync(id))
        } else {
            dispatch(getPreferencesAsync())
            dispatch(getTimelinePreferencesAsync())
            dispatch(clearAndReset())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, id])

    useEffect(() => {
        if (
            // no permission
            !graphic.data._id &&
            id &&
            graphic.status === Status.NO_GRAPHIC_PERMISSION
        ) {
            setId(null)
        }
    }, [graphic.data._id, id, graphic.status, setId])

    const { status } = useEditor(id)
    const { matchAssetsAndGraphicAssets } = useCheckEditor()
    const currentStep = useAppSelector((state) => state.editor.value.settings.currentStep)

    const navigate = useNavigate()
    const [openReadOnlyMode, setOpenReadOnlyMode] = useState<boolean>(false)
    const { expiredOwnerCompanyLicense, expiredParticipantCompanyLicense } = useWorkspacePermission(
        graphic.data.company
    )
    useEffect(() => {
        if (expiredOwnerCompanyLicense || expiredParticipantCompanyLicense) {
            setOpenReadOnlyMode(true)
        }
    }, [expiredOwnerCompanyLicense, expiredParticipantCompanyLicense])

    const handleResizeFinished = (newSizes: number[], preferencesType: SettingsKey) => {
        dispatch(setPreferencesAsync(preferencesType, newSizes))
    }

    const openCanvasSettings = useAppSelector((state) => state.editor.value.openCanvasSettings)
    const handleCloseCanvasDialog = useCallback(() => {
        dispatch(setOpenCanvasSettings(false))
    }, [dispatch])
    useEffect(() => {
        if (id === null && !currentStep.startsWith("editor")) {
            dispatch(setOpenCanvasSettings(true))
        } else {
            dispatch(setOpenCanvasSettings(false))
        }
    }, [currentStep, dispatch, id])

    const openTransitionAlert: boolean = useMemo(() => {
        return status && browserName !== BrowserTypes.Chrome ? true : false
    }, [status])

    const isDiscardContent = (canUndo || !matchAssetsAndGraphicAssets) && !openCanvasSettings
    const [promptMsg, setPromptMsg] = useState<string>("")
    const blockerFunction: BlockerFunction = (action: any) => {
        // The session expired: nothing can be saved anymore, and blocking the redirect
        // would make MainLayout's <Navigate> retry it on every render, forever.
        if (action.nextLocation.pathname === "/login") {
            return false
        }

        if (!canUndo && action.historyAction === "POP") {
            setPromptMsg("backAction")
            return true
        } else if (
            !canUndo &&
            (action.nextLocation.pathname === "/account/profile" ||
                action.nextLocation.pathname === "/app/graphics") &&
            (openReadOnlyMode || isStorageLimitExceeded)
        ) {
            setOpenReadOnlyMode(false)
            return false
        } else if (
            !canUndo &&
            action.historyAction === "PUSH" &&
            (action.nextLocation.pathname === "/app/workspaces" ||
                action.nextLocation.pathname === "/app/editor")
        ) {
            return false
        } else {
            setPromptMsg("discard")
            return isDiscardContent
        }
    }

    // Rebuild the master timeline only when the scene itself changed.
    //
    // Selecting an object and seeking must NOT trigger it: updateMasterTimeline kills and
    // recreates every tween, rewrites the inline styles of all objects and awaits font/reflow
    // work for tickers. With `selectedObjectIds` in the dependency list a single click cost a
    // full rebuild — and `updatedAt` never goes back to 0 once any object has been updated,
    // so from the first edit onwards that happened on every click.
    const updatedAt = useAppSelector((state) => selectUndoable(state).objects.updatedAt)
    const timeToSeek = useAppSelector((state) => state.masterTimeline.time)

    // masterTimeline.slice seeds an empty image src for SELECTED sequence objects, so the
    // rebuild still has to follow that part of the selection — but only that part.
    const selectedSequenceIds = useAppSelector((state) => {
        const objects = selectUndoable(state).objects.value
        return state.activeObject.selected
            .filter((id) => objects[id]?.type === "sequence")
            .join(",")
    })

    // Only passed on so the rebuilt timeline keeps its position. The GSAP playhead is already
    // there by the time setTimeToSeek is dispatched, so a change of it must not rebuild.
    const timeToSeekRef = useRef(timeToSeek)
    timeToSeekRef.current = timeToSeek

    // `lastChangedIds` only describes the change that set `updatedAt`. The effect below also
    // runs for `assets` and `selectedSequenceIds`, and for those the id list is stale — acting
    // on it would skip a rebuild that IS needed (a newly selected sequence object never gets
    // its empty image src seeded, assets never reach the sub-timelines).
    const seenUpdatedAtRef = useRef(updatedAt)

    useEffect(() => {
        // `updatedAt` announces THAT something changed; `lastChangedIds` says what. Read on
        // demand rather than subscribed — this is not another reason to run the effect, only
        // extra information about the change that already triggered it.
        const { objects } = selectUndoable(store.getState())

        const isObjectChange = seenUpdatedAtRef.current !== updatedAt
        seenUpdatedAtRef.current = updatedAt
        const changedIds = isObjectChange ? objects.lastChangedIds : null

        // 1. nothing in the changed subtree is animated -> the timeline is not affected
        if (canSkipMasterTimelineRebuild(objects, changedIds)) return
        // 2. only the tweens of the changed subtree need replacing -> do that in place
        if (dispatch(updateObjectTweens(changedIds))) return
        // 3. anything else -> full rebuild
        // the GSAP playhead is the source of truth - `timeToSeek` misses playback that stopped
        // on a pause label or at the end of the timeline
        const currentTimeline = store.getState().masterTimeline.value
        dispatch(updateMasterTimeline(currentTimeline?.time() ?? timeToSeekRef.current))
    }, [updatedAt, assets, selectedSequenceIds, store, dispatch])

    if (isMobileOnly)
        return (
            <>
                <Container component="main" maxWidth="lg">
                    <Grid
                        container
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        alignContent="stretch"
                        sx={{ height: "90vh" }}
                    >
                        <Typography variant="h2">{t("editor:mobilePageHeader")}</Typography>
                        <Typography variant="h5" mt={2} mb={4}>
                            {t("editor:mobilePageInfo")}
                        </Typography>
                    </Grid>
                </Container>
            </>
        )
    else
        return (
            <>
                <Prompt
                    when={blockerFunction}
                    title={t(`editor:${promptMsg}Title`)}
                    content={t(`editor:${promptMsg}Content`)}
                    txtAgree={t("leave")}
                    onConfirm={() => {
                        if (graphic.data.companyId) {
                            dispatch(clearCompanyStatsById({ id: graphic.data.companyId }))
                        }
                        dispatch(clearAndReset(id || undefined))
                    }}
                />
                {/**
                 * The right panel's width is kept between a minimum and a maximum by CSS on its
                 * pane (the splitter itself only knows minimums, and only while dragging); the
                 * canvas side grows or shrinks to fill whatever is left.
                 */}
                <GlobalStyles
                    styles={{
                        [`.${MAIN_PANE_CLASS}`]: { flex: "1 1 auto", minWidth: 0 },
                        [`.${RIGHT_PANE_CLASS}`]: {
                            flex: "0 0 auto",
                            minWidth: RIGHT_PANEL_MIN_WIDTH,
                            maxWidth: RIGHT_PANEL_MAX_WIDTH,
                        },
                    }}
                />
                <ReactSplit
                    direction={SplitDirection.Horizontal}
                    initialSizes={preferences.horizontalSplitterSize}
                    onResizeFinished={(_, newSizes) =>
                        handleResizeFinished(newSizes, "horizontalSplitterSize")
                    }
                    gutterClassName="panel-gutter-horizontal"
                    draggerClassName="panel-gutter-grip"
                    minWidths={[MAIN_PANE_MIN_WIDTH, RIGHT_PANEL_MIN_WIDTH]}
                    classes={[MAIN_PANE_CLASS, RIGHT_PANE_CLASS]}
                >
                    <ReactSplit
                        direction={SplitDirection.Vertical}
                        initialSizes={preferences.subVerticalSplitterSize}
                        onResizeFinished={(_, newSizes) =>
                            handleResizeFinished(newSizes, "subVerticalSplitterSize")
                        }
                        gutterClassName="panel-gutter-vertical"
                        draggerClassName="panel-gutter-grip"
                    >
                        <ReactSplit
                            direction={SplitDirection.Horizontal}
                            initialSizes={preferences.subHorizontalSplitterSize}
                            onResizeFinished={(_, newSizes) =>
                                handleResizeFinished(newSizes, "subHorizontalSplitterSize")
                            }
                            gutterClassName="panel-gutter-horizontal"
                            draggerClassName="panel-gutter-grip"
                        >
                            <LeftSideNavbar />
                            <Preview />
                        </ReactSplit>
                        <TimelineNavbar />
                    </ReactSplit>
                    <RightSideNavbar />
                </ReactSplit>
                <LoadingBackdrop open={!status} />
                <LoadingBackdropWithLabel open={loading} progress={percentLoading} />
                <CanvasSettingDialog
                    open={openCanvasSettings}
                    disabled={false}
                    hideCloseButton
                    onClose={handleCloseCanvasDialog}
                />
                {!isStorageLimitExceeded && openTransitionAlert && (
                    <TransitionAlerts
                        severity="warning"
                        alertMessage={t("editor:notSupportedBrowser")}
                    />
                )}
                <AlertDialog
                    open={openReadOnlyMode}
                    title={t("editor:readOnlyAlert.title")}
                    content={t(
                        `editor:readOnlyAlert.${
                            expiredOwnerCompanyLicense ? "owner" : "participant"
                        }.content`
                    )}
                    txtAgree={t(
                        `editor:readOnlyAlert.${
                            expiredOwnerCompanyLicense ? "owner" : "participant"
                        }.button`
                    )}
                    onAgree={() => {
                        navigate("/app/graphics")
                    }}
                />
                {!openCanvasSettings && (
                    <AlertDialog
                        open={isStorageLimitExceeded}
                        title={t("editor:storageLimitExceededAlert.title")}
                        content={t("editor:storageLimitExceededAlert.content", {
                            percent: usedStoragePercent,
                        })}
                        txtAgree={t("editor:storageLimitExceededAlert.button")}
                        onAgree={() => {
                            navigate("/app/graphics")
                        }}
                    />
                )}
                {!isOnline && <OfflineBanner />}
            </>
        )
}

export default withErrorBoundary(Editor)
